import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AxiosError } from 'axios';
import { Subject, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';
import {
  claimTypeLegalRelationship,
  legalRelationship,
} from 'src/app/components/dashboard/enums/enum-descriptors';
import { EnumsService } from 'src/app/services/enums/enums.service';
import { LegalRelationship } from 'src/types';
import { ClaimTypeLegalRelationship } from 'src/types/model/schemas';
import { PayeeMiniIconButtonComponent } from '../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeDropzoneComponent } from '../../../shared/payee-dropzone/payee-dropzone.component';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import {
  ClaimControls,
  EditCaseDialogService,
  LegalRelationshipFile,
} from '../edit-case-dialog.service';

@Component({
  selector: 'app-case-form',
  templateUrl: './case-form.component.html',
  styleUrls: ['./case-form.component.scss'],
  imports: [
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    PayeeDropzoneComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    PayeeInputDirective,
  ],
})
export class CaseFormComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private editCaseDialogService = inject(EditCaseDialogService);
  private enumsService = inject(EnumsService);

  private readonly destroy = new Subject<void>();

  claimTypeLegalRelationships: ClaimTypeLegalRelationship[] = [];
  legalRelationships: LegalRelationship[] = [];
  legalRelationshipOptions: number[] = [];

  readonly loading = signal(false);
  readonly legalRelationshipOcrLoading = signal(false);
  showLegalBaseSpecification = false;

  get form() {
    return this.editCaseDialogService.form.controls.legal_relationship;
  }
  get claims() {
    return this.editCaseDialogService.form.controls.claims;
  }
  get files() {
    return this.form.controls.files;
  }
  get specification() {
    return this.form.controls.specification;
  }
  get type_id() {
    return this.form.controls.type_id;
  }
  get invoiceItemPreview() {
    return this.editCaseDialogService.invoiceItemPreview;
  }

  async ngOnInit() {
    this.loading.set(true);
    await Promise.allSettled([
      this.enumsService
        .getEnum<LegalRelationship>(legalRelationship.model)
        .then((v) => (this.legalRelationships = v)),
      this.enumsService
        .getEnum<ClaimTypeLegalRelationship>(claimTypeLegalRelationship.model)
        .then((v) => (this.claimTypeLegalRelationships = v)),
    ]);
    this.loading.set(false);

    this.filterLegalRelationshipOptions();

    this.type_id.valueChanges
      .pipe(takeUntil(this.destroy), startWith(this.type_id.value))
      .subscribe((id) => {
        const legalRelationship = this.legalRelationships.find(
          (lr) => lr.id === id,
        );

        this.showLegalBaseSpecification = legalRelationship?.code === 999;
      });

    this.claims.valueChanges
      .pipe(
        takeUntil(this.destroy),
        map((values: FormGroup<ClaimControls>['value'][]) =>
          values.map((v) => v.type_id).join(','),
        ),
        distinctUntilChanged(),
      )
      .subscribe(() => this.filterLegalRelationshipOptions());

    void this.determineLegalRelationshipFromClaimFiles();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  legalBaseEvidencesDropped(fileList: FileList): void {
    const uploadedFiles: File[] = [];
    for (let i = 0; i < fileList.length; ++i) {
      const file = fileList.item(i) as File;
      uploadedFiles.push(file);
      this.files.push(
        this.fb.control(
          {
            file_name: file.name,
            id: null,
            storage_url: file,
          },
          { nonNullable: true },
        ),
      );
    }

    void this.runLegalRelationshipOcr(uploadedFiles[0]);
  }

  isAiFilled(control: AbstractControl | null | undefined): boolean {
    return this.editCaseDialogService.isAiFilled(control);
  }

  openLegalBaseEvidence(file: LegalRelationshipFile): void {
    if (file.storage_url instanceof File) {
      window.open(URL.createObjectURL(file.storage_url), '_blank');
    } else if (typeof file.storage_url === 'string') {
      window.open(file.storage_url, '_blank');
    }
  }

  removeLegalBaseFile(index: number): void {
    if (this.files.length <= index) {
      return;
    }
    this.files.removeAt(index);
  }

  readonly legalBaseTypeDisplayWith = (id: number): string => {
    return this.legalRelationships.find((lr) => lr.id === id)?.label || '';
  };

  private async runLegalRelationshipOcr(file?: File): Promise<void> {
    if (!file || (this.type_id.value && this.invoiceItemPreview())) {
      return;
    }

    this.legalRelationshipOcrLoading.set(true);
    try {
      const ocrData =
        await this.editCaseDialogService.getLegalRelationshipOcr(file);
      if (
        !ocrData.legal_relationship_id &&
        !ocrData.invoice_item &&
        !ocrData.claim_type_internal_id &&
        !ocrData.case_type &&
        !ocrData.contract_date_at &&
        !ocrData.contract_identifier &&
        !ocrData.claim_identification_text
      ) {
        return;
      }

      this.editCaseDialogService.applyLegalRelationshipOcr(ocrData);
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Legal relationship OCR error', error.response?.data);
      } else {
        console.error('Legal relationship OCR error', error);
      }
    } finally {
      this.legalRelationshipOcrLoading.set(false);
    }
  }

  private async determineLegalRelationshipFromClaimFiles(): Promise<void> {
    if (this.type_id.value && this.invoiceItemPreview()) {
      return;
    }

    const mediaUuids = this.claims.controls
      .flatMap((claim) => claim.controls.files.controls)
      .map((file) => file.value.id)
      .filter((id): id is string => !!id);

    if (mediaUuids.length === 0) {
      return;
    }

    this.legalRelationshipOcrLoading.set(true);
    try {
      for (const mediaUuid of mediaUuids) {
        if (this.type_id.value && this.invoiceItemPreview()) {
          return;
        }

        try {
          const ocrData =
            await this.editCaseDialogService.getLegalRelationshipOcrByMedia(
              mediaUuid,
            );
          if (
            !ocrData.legal_relationship_id &&
            !ocrData.invoice_item &&
            !ocrData.claim_type_internal_id &&
            !ocrData.case_type &&
            !ocrData.contract_date_at &&
            !ocrData.contract_identifier &&
            !ocrData.claim_identification_text
          ) {
            continue;
          }

          this.editCaseDialogService.applyLegalRelationshipOcr(ocrData);
          if (this.type_id.value && this.invoiceItemPreview()) {
            return;
          }
        } catch (error: AxiosError | unknown) {
          if (error instanceof AxiosError) {
            console.error(
              'Legal relationship OCR error',
              error.response?.data,
            );
          } else {
            console.error('Legal relationship OCR error', error);
          }
        }
      }
    } finally {
      this.legalRelationshipOcrLoading.set(false);
    }
  }

  private filterLegalRelationshipOptions(): void {
    const claimTypeIds: number[] = this.claims.value
      .map((value) => value.type_id)
      .filter((ct): ct is number => !!ct);

    if (claimTypeIds.length === 0) {
      this.legalRelationshipOptions = this.legalRelationships.map(
        (lr) => lr.id,
      );
    } else {
      this.legalRelationshipOptions = this.legalRelationships
        .map((lr) => lr.id)
        .filter((lr) =>
          this.claimTypeLegalRelationships.some(
            (ctlr) =>
              ctlr.legal_relationship_id === lr &&
              claimTypeIds.includes(ctlr.claim_type_id),
          ),
        );
    }
  }
}
